import { KeyboardEvent } from "react";
import { Select } from "antd";
import { CloseCircleFilled } from "@ant-design/icons";
import { debounce } from "lodash";
import { SelectionInputProps } from "./types";
import styles from "./styles.module.scss";

export default function SelectionInput({
  containerStyle,
  onChange,
  value,
  options,
  disabled = false,
  onSearch,
  onBlur,
  placeholder,
  mode = undefined,
  showSearch = false,
  isLoading = false,
  isScrollable = false,
  allowClear = false,
}: SelectionInputProps) {
  const id = `selectInput${Math.random()}`;
  // Count total options including those inside groups
  const totalOptions = (options as any[])?.reduce((count: number, opt: any) => {
    return count + (opt.options ? opt.options.length : 1);
  }, 0) ?? 0;
  const should_show_search = showSearch || totalOptions >= 10;
  const debouncedOnSearch = onSearch ? debounce(onSearch, 300) : undefined;

  // antd's clear icon only reacts to mousedown, so make it focusable and
  // clearable with Enter/Space for keyboard users.
  const onClearKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    event.stopPropagation();
    // @ts-ignore
    onChange?.(mode === "multiple" ? [] : undefined);
  };

  const clearIcon = (
    <span
      role="button"
      tabIndex={0}
      aria-label={`Clear ${placeholder ?? "selection"}`}
      onKeyDown={onClearKeyDown}
    >
      <CloseCircleFilled />
    </span>
  );

  return (
    <div
      className={`${styles.container} ${isScrollable ? styles.scrollable : ''} ${allowClear ? styles.clearable : ''} ${containerStyle}`}
      id={id}
    >
      <Select
        getPopupContainer={(trigger: HTMLElement | null) => {
          return (
            (trigger?.parentNode as HTMLElement) ?? document.getElementById(id)
          );
        }}
        mode={mode}
        showSearch={should_show_search}
        optionFilterProp="label"
        // With onSearch the server already filtered (e.g. by project_id), so
        // re-filtering by label would hide matches whose label lacks the term
        filterOption={onSearch ? false : undefined}
        loading={isLoading}
        value={value}
        onChange={onChange}
        onSearch={debouncedOnSearch}
        onBlur={onBlur}
        // @ts-ignore
        options={options}
        disabled={disabled}
        placeholder={placeholder}
        allowClear={allowClear && { clearIcon }}
      />
    </div>
  );
}
